/**
 * O menu com ramos (#1012): no mapa, o bloco "Enviar menu de opções" tem uma
 * saída por opção ("Agendar consulta", "Agendar exame"…) e, com prazo, a saída
 * "Sem resposta". A pessoa liga cada opção ao próximo passo, sem escrever nada.
 *
 * ── O que vai para o serviço ─────────────────────────────────────────────
 *
 * O serviço não tem esse bloco: ele tem os três de que o menu é feito. Ao
 * salvar, o menu `X` vira:
 *
 *   X              enviar o menu (a ação de sempre)
 *   X__resposta    esperar "O cliente escolheu uma opção do menu" da mesma
 *                  conversa, com o prazo; sem resposta, `tempo_esgotado`
 *   X__opcao       escolha pela opção (`evento.reply.option_id`), um caso por
 *                  opção
 *
 * Ao abrir, os três voltam a ser um bloco só, reconhecidos pelos ids e pela
 * forma. Um menu sem ramo ligado e sem prazo vai como sempre foi: só a ação,
 * com "Próximo".
 */

import { opcoesDoMenu } from './parametros';
import type { AutomacaoDaApi } from '@cliqcrm/contracts';
import type { LigacaoDoGrafo, NoDoGrafo } from './mapa';

/* A configuração da tela (`edicao.ts`), pela forma: importar de lá faria um ciclo. */
type Configuracao = { grafo: NonNullable<AutomacaoDaApi['grafo']> };

/* Os mesmos de `edicao.ts`, aqui sem importar de lá: `edicao` usa este arquivo. */
const PROXIMO = 'padrao';
const saidaDaLigacao = (l: LigacaoDoGrafo): string => l.quando ?? PROXIMO;

const RESPOSTA = '__resposta';
const OPCAO = '__opcao';

/** O parâmetro que guarda o prazo da saída "Sem resposta", em ms. Só da tela: não vai ao serviço. */
export const PRAZO_SEM_RESPOSTA = '__sem_resposta_ms';

const EVENTO_DA_RESPOSTA = 'conversa.resposta_de_botao';
const EXPRESSAO_DA_OPCAO = 'evento.reply.option_id';
/** O evento esperado é da mesma conversa: a referência "A conversa do evento". */
const CORRELACAO_PADRAO = 'conversation_id';

export const SEM_RESPOSTA = 'tempo_esgotado';

type No = NoDoGrafo;
type NoDeAcao = Extract<No, { tipo: 'acao' }>;

export function ehMenu(no: No | undefined): no is NoDeAcao {
  return no?.tipo === 'acao' && no.acao === 'enviar_menu';
}

/** O id do bloco que a tela mostra, para um id do serviço (os do menu viram o menu). */
export function idNaTela(id: string): string {
  if (id.endsWith(RESPOSTA)) return id.slice(0, -RESPOSTA.length);
  if (id.endsWith(OPCAO)) return id.slice(0, -OPCAO.length);
  return id;
}

/** O prazo da saída "Sem resposta", em milissegundos; `null` sem prazo. */
export function prazoSemResposta(no: NoDeAcao): number | null {
  const ms = Number(no.parametros[PRAZO_SEM_RESPOSTA] ?? '');
  return Number.isFinite(ms) && ms > 0 ? Math.round(ms) : null;
}

/** As saídas do menu: uma por opção e, com prazo, "Sem resposta". */
export function saidasDoMenu(no: NoDeAcao): string[] {
  const opcoes = opcoesDoMenu(no.parametros['opcoes'] ?? '') ?? [];
  return [
    ...opcoes.filter((o) => o.id !== '').map((o) => `caso:${o.id}`),
    ...(prazoSemResposta(no) ? [SEM_RESPOSTA] : []),
  ];
}

/** O nome de uma saída do menu: o rótulo da opção, ou "Sem resposta". */
export function rotuloDaSaidaDoMenu(no: NoDeAcao, saida: string): string | null {
  if (saida === SEM_RESPOSTA) return 'Sem resposta';
  if (!saida.startsWith('caso:')) return null;
  const id = saida.slice('caso:'.length);
  const opcao = (opcoesDoMenu(no.parametros['opcoes'] ?? '') ?? []).find((o) => o.id === id);
  return opcao?.rotulo.trim() || id;
}

/**
 * O menu tem ramos: alguma opção ligada, ou o prazo de "Sem resposta". Sem
 * eles, é o menu de antes: manda e segue pelo "Próximo", sem esperar.
 */
export function temRamos(no: NoDeAcao, ligacoes: readonly LigacaoDoGrafo[]): boolean {
  return (
    prazoSemResposta(no) !== null ||
    ligacoes.some((l) => l.de === no.id && Boolean(l.quando?.startsWith('caso:')))
  );
}

/**
 * Só "A conversa do evento": a espera reconhece a resposta pela conversa do
 * evento que começou a automação, e outra conversa nunca casaria.
 */
export function conversaDoMenuValida(no: NoDeAcao): boolean {
  return (no.parametros['conversa_id'] ?? '').trim() === CORRELACAO_PADRAO;
}

// ── Do serviço para a tela ───────────────────────────────────────────────

/** Junta cada menu com a espera e a escolha dele num bloco só. */
export function comporMenus<C extends Configuracao>(c: C): C {
  const nos = c.grafo.nos;
  const porId = new Map(nos.map((n) => [n.id, n]));
  const ligacoes = c.grafo.ligacoes;
  const sai = (de: string, saida: string) =>
    ligacoes.find((l) => l.de === de && saidaDaLigacao(l) === saida);

  const helpers = new Set<string>();
  const novas: LigacaoDoGrafo[] = [];
  const prazos = new Map<string, number>();

  for (const no of nos) {
    if (!ehMenu(no)) continue;
    const espera = porId.get(no.id + RESPOSTA);
    const escolha = porId.get(no.id + OPCAO);
    const formaCerta =
      espera?.tipo === 'esperar_evento' &&
      espera.evento === EVENTO_DA_RESPOSTA &&
      escolha?.tipo === 'escolha' &&
      escolha.expressao.trim() === EXPRESSAO_DA_OPCAO &&
      sai(no.id, PROXIMO)?.para === espera.id &&
      sai(espera.id, PROXIMO)?.para === escolha.id;
    if (!formaCerta) continue;

    helpers.add(espera.id);
    helpers.add(escolha.id);
    if (espera.limite_ms) prazos.set(no.id, espera.limite_ms);
    for (const l of ligacoes) {
      if (l.de === escolha.id && l.quando?.startsWith('caso:')) {
        novas.push({ ...l, de: no.id });
      } else if (l.de === escolha.id && saidaDaLigacao(l) === PROXIMO) {
        // "Outra resposta": o que não é opção deste menu volta a ser o "Próximo".
        novas.push({ de: no.id, para: l.para });
      } else if (l.de === espera.id && l.quando === SEM_RESPOSTA) {
        novas.push({ ...l, de: no.id });
      }
    }
  }
  if (helpers.size === 0) return c;

  const ehDoMenu = (l: LigacaoDoGrafo) => helpers.has(l.de) || helpers.has(l.para);
  return {
    ...c,
    grafo: {
      ...c.grafo,
      nos: nos
        .filter((n) => !helpers.has(n.id))
        .map((n) =>
          ehMenu(n) && prazos.has(n.id)
            ? {
                ...n,
                parametros: { ...n.parametros, [PRAZO_SEM_RESPOSTA]: String(prazos.get(n.id)) },
              }
            : n,
        ),
      ligacoes: [...ligacoes.filter((l) => !ehDoMenu(l)), ...novas],
    },
  };
}

// ── Da tela para o serviço ───────────────────────────────────────────────

/** Separa cada menu com ramos na ação, na espera e na escolha que o serviço roda. */
export function expandirMenus<C extends Configuracao>(c: C): C {
  const nos: No[] = [];
  const ligacoes: LigacaoDoGrafo[] = [];
  const doMenu = new Map<string, NoDeAcao>();

  for (const no of c.grafo.nos) {
    if (!ehMenu(no)) {
      nos.push(no);
      continue;
    }
    const { [PRAZO_SEM_RESPOSTA]: _prazo, ...parametros } = no.parametros;
    const limpo: NoDeAcao = { ...no, parametros };
    const temRamo = temRamos(no, c.grafo.ligacoes);
    nos.push(limpo);
    if (!temRamo && !prazoSemResposta(no)) continue;

    doMenu.set(no.id, no);
    const prazo = prazoSemResposta(no);
    const x = no.posicao?.x ?? 0;
    const y = no.posicao?.y ?? 0;
    nos.push({
      id: no.id + RESPOSTA,
      tipo: 'esperar_evento',
      evento: EVENTO_DA_RESPOSTA,
      // A conversa do evento: o mesmo caminho vale na automação e na resposta.
      correlacao: CORRELACAO_PADRAO,
      ...(prazo ? { limite_ms: prazo } : {}),
      posicao: { x: x + 120, y: y + 140 },
    } as No);
    nos.push({
      id: no.id + OPCAO,
      tipo: 'escolha',
      expressao: EXPRESSAO_DA_OPCAO,
      posicao: { x: x + 240, y: y + 140 },
    } as No);
    ligacoes.push({ de: no.id, para: no.id + RESPOSTA });
    ligacoes.push({ de: no.id + RESPOSTA, para: no.id + OPCAO });
  }

  for (const l of c.grafo.ligacoes) {
    if (!doMenu.has(l.de)) {
      ligacoes.push(l);
    } else if (l.quando === SEM_RESPOSTA) {
      // Sem prazo, não há "Sem resposta": a ligação que sobrou de antes cai.
      if (prazoSemResposta(doMenu.get(l.de)!)) ligacoes.push({ ...l, de: l.de + RESPOSTA });
    } else if (l.quando?.startsWith('caso:')) {
      ligacoes.push({ ...l, de: l.de + OPCAO });
    } else {
      // O "Próximo" de um menu com ramos é a resposta que não é opção dele.
      ligacoes.push({ de: l.de + OPCAO, para: l.para });
    }
  }

  return { ...c, grafo: { ...c.grafo, nos, ligacoes } };
}
